import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { calculateNextRoundGoal } from './calculateNextRoundGoal';
import { getDisplayablePetitionPhoto } from './getDisplayablePetitionPhoto';
import { normalizeJoinTheCommunityCustomConfig } from './normalize/normalizeJoinTheCommunityCustomConfig';
import { SOCIAL_STARTER_CONTROL_EXPERIMENTS } from './normalize/socialStarterControlExperiments';
import { smallestChipInAmount } from './smallestChipInAmount';
import type {
	FcmValues,
	Petition,
	PromotionStats,
	PsfPromoteOrSharePageData,
	Recruiter,
	SocialStarterExperiments,
	Viewer,
} from './types';

/** FIXME same estimation hack as normalizeData's SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE. */
const SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE = 0.6;

type Params = {
	petition: Petition;
	promotionStats: PromotionStats;
	viewer: Viewer;
	recruiter?: Recruiter;
	featureConfigs: FcmValues;
	experiments: SocialStarterExperiments;
};

/**
 * Social Starter's own page-data normalizer. Every combo experiment the Social Starter query never
 * resolves is forced to a static control (SOCIAL_STARTER_CONTROL_EXPERIMENTS) — this must never call
 * combo's treating normalizer (normalizePromoteShareOnComboExperiment) or treatExperiment, since the
 * Social Starter query's fields
 * aren't queried with treat:true and can't back them.
 */
export function normalizeSocialStarterData(
	utilityContext: UtilityContext,
	{ petition, promotionStats, viewer, recruiter, featureConfigs, experiments }: Params,
): PsfPromoteOrSharePageData {
	const { tagsConnection, ...rest } = petition;
	const normalizedCategoryTags = tagsConnection?.nodes.map((tag) => tag.name) ?? [];

	const signaturesFromShareCountEstimate = Math.round(
		petition.signatureState.signatureCount.displayed * SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE,
	);

	const { goal: promotionGoal, goalLocalized: promotionGoalLocalized } = normalizeGoal(
		utilityContext,
		promotionStats.promoterCount,
	);

	const viewerCurrency = viewer.country.currency || { code: 'USD', exchangeRate: 1 };

	return {
		petition: { ...rest, promotionStats, categoryTags: normalizedCategoryTags },
		recruiter,
		joinTheCommunityCustomConfig: normalizeJoinTheCommunityCustomConfig(utilityContext, {
			viewer,
			fcmValues: featureConfigs,
		}),
		...smallestChipInAmount(utilityContext, viewerCurrency, viewer.country.promotionCountrySettings?.amountChoices),
		promotionGoal,
		promotionGoalLocalized,
		...SOCIAL_STARTER_CONTROL_EXPERIMENTS,
		psfShareFirstSocialStarterExperiment: experiments.psfShareFirstSocialStarterExperiment,
		psfComboBanditVariation: null,
		hideMediaSlot: !getDisplayablePetitionPhoto(petition.photo),
		hideRecruiterEnabled: featureConfigs.hideRecruiterEnabled,
		isShareSlideUpEnabled: featureConfigs.isShareSlideUpEnabled,
		isShareAssetsEnabled: featureConfigs.isShareAssetsEnabled,
		channelAwareCtaChannels: featureConfigs.channelAwareCtaChannels,
		// Off here: the post sheet belongs to a combo experiment that Social Starter signers must not enter.
		facebookPostSheetFcm: null,
		campaignActionsEnabled: Boolean(featureConfigs.campaignActionsConfig?.enabled),
		useDonationLanguage: featureConfigs.useDonationLanguage,
		advocateEntryPointButtonLabel: featureConfigs.advocateEntryPointConfig.buttonLabel,
		advocateEntryPointCpDisabled: featureConfigs.advocateEntryPointConfig.disabledCps.includes(petition.slug),
		signaturesFromShareCountEstimateLocalized: utilityContext.i18n.localizeNumber(signaturesFromShareCountEstimate),
		promoteFormRawData: {
			viewer,
			featureConfigs,
		},
	};
}

function normalizeGoal({ i18n }: UtilityContext, count: number) {
	const goal = calculateNextRoundGoal(count);

	return {
		goal,
		goalLocalized: i18n.localizeNumber(goal),
	};
}
